import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Typography,
  Table,
  Tag,
  Button,
  Space,
  Card,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Modal,
  Descriptions,
  theme
} from 'antd'
import {
  FileTextOutlined,
  EyeOutlined,
  FilterOutlined,
  ClearOutlined,
  TableOutlined,
  AppstoreOutlined,
  UnorderedListOutlined
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { apiService, User } from '@/lib/api'
import DashboardLayout from '@/components/DashboardLayout'
import dayjs from 'dayjs'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

interface Transaction {
  id: number
  reference_number: string
  transaction_date: string
  transaction_type: string
  description: string
  total_amount: number
  status: string
  total_debits: number
  total_credits: number
  is_balanced: boolean
  created_by_name: string | null
  related_document?: {
    type: string
    id: number
    number: string
  }
}

interface TransactionLine {
  id: number
  chart_of_account_id: number
  account_code: string
  account_name: string
  account_type: string
  debit_amount: number
  credit_amount: number
  description: string
}

interface TransactionDetail extends Transaction {
  transaction_lines: TransactionLine[]
}

interface TransactionsResponse {
  success: boolean
  message?: string
  data?: Transaction[]
  pagination?: {
    page: number
    per_page: number
    total: number
    total_pages: number
    total_count?: number
  }
}

export default function TransactionsPage() {
  const navigate = useNavigate()
  const { token } = theme.useToken()
  const [user, setUser] = useState<User | null>(null)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(20)
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [typeFilter, setTypeFilter] = useState<string | undefined>()
  const [dateRange, setDateRange] = useState<[string, string] | undefined>()
  const [selectedTransaction, setSelectedTransaction] = useState<number | null>(null)
  const [lineItemView, setLineItemView] = useState<'table' | 'taccount' | 'journal'>('table')

  useEffect(() => {
    const storedUser = localStorage.getItem('user')
    if (storedUser) {
      setUser(JSON.parse(storedUser))
    }
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('user')
    navigate('/')
  }

  const { data: transactionsData, isLoading, refetch } = useQuery<TransactionsResponse>({
    queryKey: ['transactions', page, perPage, statusFilter, typeFilter, dateRange],
    queryFn: async () => {
      const params: any = {
        page: page.toString(),
        per_page: perPage.toString(),
      }
      if (statusFilter) params.status = statusFilter
      if (typeFilter) params.transaction_type = typeFilter
      if (dateRange) {
        params.start_date = dateRange[0]
        params.end_date = dateRange[1]
      }
      const response = await apiService.getTransactions(params)
      if (response.success && response.data) {
        return response as TransactionsResponse
      }
      throw new Error(response.message || 'Failed to load transactions')
    }
  })

  const { data: transactionDetail, isLoading: isLoadingDetail } = useQuery<TransactionDetail | null>({
    queryKey: ['transaction', selectedTransaction],
    queryFn: async () => {
      if (!selectedTransaction) return null
      const response = await apiService.getTransaction(selectedTransaction.toString())
      if (response.success && response.data) {
        return response.data as TransactionDetail
      }
      throw new Error(response.message || 'Failed to load transaction details')
    },
    enabled: !!selectedTransaction
  })

  const handleClearFilters = () => {
    setStatusFilter(undefined)
    setTypeFilter(undefined)
    setDateRange(undefined)
    setPage(1)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'posted': return 'green'
      case 'draft': return 'orange'
      case 'void': return 'red'
      default: return 'default'
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Invoice': return 'blue'
      case 'Bill': return 'purple'
      case 'Payment': return 'green'
      case 'Bill Payment': return 'cyan'
      case 'Credit Note': return 'orange'
      case 'Journal Entry': return 'default'
      default: return 'default'
    }
  }

  const columns = [
    { title: 'Reference', dataIndex: 'reference_number', key: 'reference_number', render: (text: string) => <Text strong>{text}</Text> },
    { title: 'Date', dataIndex: 'transaction_date', key: 'transaction_date', render: (date: string) => dayjs(date).format('DD MMM YYYY') },
    { title: 'Type', dataIndex: 'transaction_type', key: 'transaction_type', render: (type: string) => <Tag color={getTypeColor(type)}>{type}</Tag> },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    { title: 'Amount', dataIndex: 'total_amount', key: 'total_amount', align: 'right' as const, render: (amount: number) => <Text strong style={{ color: token.colorPrimary }}>MYR {Number(amount).toFixed(2)}</Text> },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (status: string) => <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag> },
    { title: 'Related Doc', dataIndex: 'related_document', key: 'related_document', render: (doc: any) => doc ? <Text type="secondary">{doc.number}</Text> : '-' },
    { title: 'Action', key: 'action', render: (_: any, record: Transaction) => <Button type="link" icon={<EyeOutlined />} onClick={() => setSelectedTransaction(record.id)}>View</Button> }
  ]

  const totalDebits = transactionsData?.data?.reduce((sum: number, t: Transaction) => sum + Number(t.total_debits), 0) || 0
  const totalCredits = transactionsData?.data?.reduce((sum: number, t: Transaction) => sum + Number(t.total_credits), 0) || 0

  return (
    <DashboardLayout user={user} onLogout={handleLogout} title="Transactions" subtitle="View and manage all your transactions" titleIcon={<FileTextOutlined />}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}><Card><Statistic title="Total Transactions" value={transactionsData?.pagination?.total_count || 0} prefix={<FileTextOutlined />} /></Card></Col>
          <Col xs={24} sm={8}><Card><Statistic title="Total Debits" value={totalDebits} precision={2} prefix="MYR" valueStyle={{ color: token.colorError }} /></Card></Col>
          <Col xs={24} sm={8}><Card><Statistic title="Total Credits" value={totalCredits} precision={2} prefix="MYR" valueStyle={{ color: token.colorSuccess }} /></Card></Col>
        </Row>

        <Card title={<><FilterOutlined /> Filters</>}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Select placeholder="Status" style={{ width: '100%' }} allowClear value={statusFilter} onChange={setStatusFilter} options={[{ value: 'draft', label: 'Draft' }, { value: 'posted', label: 'Posted' }, { value: 'void', label: 'Void' }]} />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select placeholder="Transaction Type" style={{ width: '100%' }} allowClear value={typeFilter} onChange={setTypeFilter} options={[{ value: 'Invoice', label: 'Invoice' }, { value: 'Bill', label: 'Bill' }, { value: 'Payment', label: 'Payment' }, { value: 'Bill Payment', label: 'Bill Payment' }, { value: 'Credit Note', label: 'Credit Note' }, { value: 'Journal Entry', label: 'Journal Entry' }]} />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <RangePicker style={{ width: '100%' }} value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null} onChange={(dates) => { if (dates && dates[0] && dates[1]) { setDateRange([dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]) } else { setDateRange(undefined) } }} />
            </Col>
            <Col xs={24} sm={12} md={4}><Button icon={<ClearOutlined />} onClick={handleClearFilters} block>Clear Filters</Button></Col>
          </Row>
        </Card>

        <Card>
          <Table columns={columns} dataSource={transactionsData?.data || []} rowKey="id" loading={isLoading} scroll={{ x: 800 }} pagination={{ current: page, pageSize: perPage, total: transactionsData?.pagination?.total_count || 0, showSizeChanger: true, showTotal: (total) => `Total ${total} transactions`, onChange: (newPage, newPageSize) => { setPage(newPage); setPerPage(newPageSize) } }} />
        </Card>
      </Space>

      <Modal title={`Transaction Details - ${transactionDetail?.reference_number || ''}`} open={selectedTransaction !== null} onCancel={() => setSelectedTransaction(null)} footer={[<Button key="close" onClick={() => setSelectedTransaction(null)}>Close</Button>]} width={900}>
        {isLoadingDetail ? (
          <div style={{ textAlign: 'center', padding: 24 }}>Loading...</div>
        ) : transactionDetail ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Reference">{transactionDetail.reference_number}</Descriptions.Item>
              <Descriptions.Item label="Date">{dayjs(transactionDetail.transaction_date).format('DD MMM YYYY')}</Descriptions.Item>
              <Descriptions.Item label="Type"><Tag color={getTypeColor(transactionDetail.transaction_type)}>{transactionDetail.transaction_type}</Tag></Descriptions.Item>
              <Descriptions.Item label="Status"><Tag color={getStatusColor(transactionDetail.status)}>{transactionDetail.status.toUpperCase()}</Tag></Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>{transactionDetail.description}</Descriptions.Item>
              <Descriptions.Item label="Total Amount">MYR {Number(transactionDetail.total_amount).toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="Balanced">{transactionDetail.is_balanced ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>}</Descriptions.Item>
            </Descriptions>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={5} style={{ margin: 0 }}>Transaction Lines</Title>
                <Space>
                  <Button type={lineItemView === 'table' ? 'primary' : 'default'} icon={<TableOutlined />} onClick={() => setLineItemView('table')}>Table</Button>
                  <Button type={lineItemView === 'taccount' ? 'primary' : 'default'} icon={<AppstoreOutlined />} onClick={() => setLineItemView('taccount')}>T-Account</Button>
                  <Button type={lineItemView === 'journal' ? 'primary' : 'default'} icon={<UnorderedListOutlined />} onClick={() => setLineItemView('journal')}>Journal</Button>
                </Space>
              </div>

              {lineItemView === 'table' && (
                <Table size="small" dataSource={transactionDetail.transaction_lines || []} rowKey="id" pagination={false} columns={[
                  { title: 'Account', key: 'account', render: (_: any, record: TransactionLine) => <div><Text strong>{record.account_code}</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>{record.account_name}</Text></div> },
                  { title: 'Type', dataIndex: 'account_type', key: 'account_type', render: (type: string) => <Tag>{type}</Tag> },
                  { title: 'Description', dataIndex: 'description', key: 'description' },
                  { title: 'Debit', dataIndex: 'debit_amount', key: 'debit_amount', align: 'right' as const, render: (amount: number) => amount > 0 ? <Text style={{ color: token.colorError }}>{Number(amount).toFixed(2)}</Text> : '-' },
                  { title: 'Credit', dataIndex: 'credit_amount', key: 'credit_amount', align: 'right' as const, render: (amount: number) => amount > 0 ? <Text style={{ color: token.colorSuccess }}>{Number(amount).toFixed(2)}</Text> : '-' }
                ]} />
              )}
            </div>
          </Space>
        ) : null}
      </Modal>
    </DashboardLayout>
  )
}
